<template>
    <!-- Barn row: a genuine <tr> inside Index.vue's single grouped <table>. Columns are shared with
         the header via that table's <colgroup>. An expandable Barn gets a chevron; a non-expandable
         ("aggregated") Barn shows only its own figures, no chevron. The collapsed-and-deviating badge
         stays on the headline even once expanded (spec), so it isn't gated on `!expanded`.

         Deviation verdict is a coloured left-accent border on the Name cell only (not the whole row -
         every cell in a <tr> shares that row's height, so the visual result is identical to a full-row
         accent). No other borders anywhere: row separation is whitespace + the hover background alone. -->
    <tr class="text-sm transition-colors hover:bg-gray-50">
        <td class="border-l-2 py-1 pl-6 pr-2" :class="rowClasses">
            <div class="flex items-center gap-2">
                <button
                    v-if="barn.isExpandable"
                    type="button"
                    class="grid h-5 w-5 shrink-0 place-content-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    :title="expanded ? $t('views.objectives.texts.collapse') : $t('views.objectives.texts.expand')"
                    :aria-expanded="expanded"
                    :aria-label="`${expanded ? $t('views.objectives.texts.collapse') : $t('views.objectives.texts.expand')}: ${barn.name}`"
                    @click="emit('toggle')"
                >
                    <ChevronRight class="h-4 w-4 transition-transform" :class="{ 'rotate-90': expanded }" aria-hidden="true" />
                </button>
                <span class="min-w-0 truncate text-[13.5px] font-semibold" :class="hasNoValue ? 'text-gray-400' : 'text-gray-900'" :title="nameTooltip">{{ barn.name }}</span>
                <!-- After the name, not before: a leading badge would push every Barn's name over by a
                     variable amount, so names wouldn't line up with each other down the column -
                     trailing keeps every name's own start position fixed. -->
                <span
                    v-if="deviatingSectionCount > 0"
                    class="grid h-5 min-w-[20px] shrink-0 place-content-center rounded-full bg-red-600 px-1 text-[11px] font-bold text-white"
                    :title="$tc('views.objectives.texts.deviationBadgeTitle', deviatingSectionCount)"
                    >{{ deviatingSectionCount }}</span
                >
            </div>
        </td>

        <!-- Mortality: bar + value together in one column rather than split - they read as a single
             figure. -->
        <td class="py-1 pr-2 tabular-nums">
            <div class="flex items-center gap-2">
                <template v-if="barn.currentValue != null && barn.resolvedTarget != null">
                    <ObjectiveBar :current-value="barn.currentValue" :resolved-target="barn.resolvedTarget" :target-source="barn.status.target_source" :ceiling="ceiling" class="w-20" />
                    <span class="text-[13.5px] font-bold leading-none" :class="valueColourClass"
                        >{{ $n(barn.currentValue, 'decimal') }}%<span v-if="isBarOverflow(barn.currentValue, ceiling)">&raquo;</span></span
                    >
                </template>
                <!-- No target anywhere (Farm override or Directory default) - nothing to draw a bar or
                     a colour verdict from, and no mortality estimate shown either way: a number with
                     no target to judge it against reads as more confident than it is. Offer the fix
                     directly instead - "Set" opens the Targets modal pre-focused on this Barn's Farm. -->
                <template v-else-if="barn.resolvedTarget == null">
                    <button
                        type="button"
                        class="shrink-0 rounded-md border border-primary-600 px-2 py-1 text-[12px] font-medium text-primary-700 transition-colors hover:bg-primary-50"
                        @click="emit('setTarget', farmLocationId)"
                    >
                        {{ $t('views.objectives.texts.setTarget') }}
                    </button>
                </template>
                <!-- Target is set, but no measured value at all (Empty/InsufficientPigsInWindow/
                     DaysInPiggeryUnavailable) - a bare dash, the reason lives on the name's tooltip
                     instead of as inline text: an inline "No pigs in this window" note would read as
                     more alarming than a greyed-out name and a hover explanation (see
                     hasNoValue/nameTooltip). -->
                <span v-else class="text-[12.5px] font-medium text-gray-500">{{ placeholder }}</span>
            </div>
        </td>

        <!-- Trend (gamification): latest vs previous bucket, off the same history query the
             trend chart uses - empty (no icon) whenever there's under two known buckets to compare,
             same as the chart's own "not enough history" gate. Its own column, distinct from the
             value next to it since they're two different signals. TrendingUp/TrendingDown rather than
             a plain ▲/▼ glyph. -->
        <td class="py-1 pr-4 tabular-nums">
            <component
                :is="trendDirection === 'improved' ? TrendingDown : TrendingUp"
                v-if="trendDirection != null"
                class="h-4 w-4"
                :class="trendDirectionColourClass(trendDirection)"
                :title="trendDirection === 'improved' ? $t('views.objectives.texts.trendImprovedTooltip') : $t('views.objectives.texts.trendWorsenedTooltip')"
                aria-hidden="true"
            />
        </td>

        <!-- Pigs to Target - its own column, only ever non-null alongside a resolved Mortality
             figure since the backend gates it on the same two values. -->
        <td class="py-1 pr-2 tabular-nums">
            <span
                v-if="barn.pigsToTarget != null"
                class="text-[11.5px] font-semibold"
                :class="pigsToTargetColourClass(barn.pigsToTarget)"
                :title="pigsToTargetTooltipKey(barn.pigsToTarget) ? $tc(`views.objectives.texts.${pigsToTargetTooltipKey(barn.pigsToTarget)}`, Math.abs(barn.pigsToTarget)) : undefined"
            >
                {{ formatPigsToTarget(barn.pigsToTarget) }}
            </span>
        </td>

        <td class="py-1 pr-1 text-right tabular-nums">
            <span class="text-[11.5px] font-medium text-gray-400">{{ $t('views.objectives.texts.deadCountLabel', { count: barn.deadCount }) }}</span>
        </td>
    </tr>

    <!-- Section child rows: rendered only once the Barn is both expandable and expanded. A <template>
         here is a pure grouping wrapper (renders no DOM node of its own), so this component ends up
         contributing several sibling <tr>s to the table - completely normal for a Vue 3 multi-root
         template mounted straight into a <tbody>, no wrapping element needed. -->
    <template v-if="barn.isExpandable && expanded">
        <SectionRow v-for="section in barn.sections" :key="section.id" :section="section" :ceiling="ceiling" />
        <tr v-if="barn.sections.length === 0">
            <td colspan="5" class="py-2 pl-6 text-xs italic text-gray-400">{{ $t('views.objectives.texts.noSections') }}</td>
        </tr>
    </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronRight, TrendingDown, TrendingUp } from 'lucide-vue-next';
import type { BarnObjectiveRow, TrendDirection } from './objectiveRow';
import { objectiveValueColourClass, pigsToTargetColourClass, pigsToTargetTooltipKey, formatPigsToTarget, trendDirectionColourClass } from './objectiveDisplay';
import { countDeviatingSections, isInDeviation, isWithinTarget } from './deviation';
import { isBarOverflow } from './objectiveBar';
import SectionRow from './SectionRow.vue';
import ObjectiveBar from './ObjectiveBar.vue';

const props = defineProps<{
    barn: BarnObjectiveRow;
    expanded: boolean;
    ceiling: number;
    // The Farm this Barn belongs to - not part of BarnObjectiveRow itself (Farms are grouping headers
    // with no row of their own), needed only so the "Set" button knows which Farm's override to open.
    farmLocationId: number;
    // Improvement arrow (gamification) - computed by the caller (Index.vue) from the
    // separate history query, not looked up here; BarnRow only knows how to render the verdict, not
    // where the history data lives.
    trendDirection: TrendDirection | null;
}>();

const emit = defineEmits<{
    toggle: [];
    setTarget: [farmLocationId: number];
}>();

const placeholder = '—';

const valueColourClass = computed(() => objectiveValueColourClass(props.barn));
const deviatingSectionCount = computed(() => countDeviatingSections(props.barn.sections));

// No measured value at all (Empty/InsufficientPigsInWindow/DaysInPiggeryUnavailable/NoData) - greys
// the name out instead of the row carrying its own separate "No pigs in this window"-style note,
// since that note would read as an alarm rather than a data-quality caveat. The reason itself only
// ever surfaces on hover, folded into the same tooltip the name already had for truncation.
const hasNoValue = computed(() => props.barn.currentValue == null);
const nameTooltip = computed(() => (hasNoValue.value && props.barn.status.message ? `${props.barn.name} — ${props.barn.status.message}` : props.barn.name));

// Just the left-accent colour (see the template's own note on the grouped table) - the
// neutral case needs no override, the Name cell's own border-l-2 already defaults to a light neutral
// via Tailwind's default border colour.
const rowClasses = computed(() => {
    if (isInDeviation(props.barn)) return 'border-l-red-400';
    if (isWithinTarget(props.barn)) return 'border-l-green-400';
    return 'border-l-gray-100';
});
</script>
